<template>
  <eg-modal
    id="create-edit-source-material-modal"
    :title="modalTitle"
    :closable="true"
    :value="true"
    size="large"
    style="z-index: var(--layout-z-index-modal)"
    @open="onOpenModalHandler"
    @input="$emit('update:isShown', $event)"
    @close="$emit('close')"
  >
    <div v-if="isLoading" class="text-center">
      <eg-b-spinner class="mb-3"></eg-b-spinner>
      <p>{{ $t("materials.createEditModal.loading") }}</p>
    </div>

    <div v-else>
      <source-material-preview-form
        v-if="readOnly"
        :source-material="editableItem"
      />
      <eg-b-form v-else>
        <eg-form-group :label="$t('common.name')" required>
          <eg-b-form-input
            v-model="editableItem.title"
            placeholder=""
            type="text"
          />
        </eg-form-group>
        <eg-form-group
          :label="$t('materials.createEditModal.writingPrompt')"
          optional-label
        >
          <eg-b-form-textarea
            v-model="editableItem.tdaInstructions"
            :rows="5"
            max-rows="12"
            class="custom-textarea"
            :placeholder="
              $t('materials.createEditModal.writingPromptPlaceholder')
            "
          ></eg-b-form-textarea>
        </eg-form-group>
        <eg-form-group
          :label="$t('materials.createEditModal.referenceMaterials')"
          :description="
            $t('materials.createEditModal.referenceMaterialsDescription')
          "
          lass="m-0"
          optional-label
        >
          <tda-sources
            ref="tdaSources"
            :value="editableItem.tdaSources"
            @input="updateTda"
            @file-added="uppyFiles = $event"
            @file-removed="uppyFiles = $event"
          />
        </eg-form-group>
      </eg-b-form>
    </div>

    <template #footer="{ close }">
      <eg-button type="secondary" @click="close">{{
        $t("common.close")
      }}</eg-button>
      <eg-button
        v-if="!readOnly"
        type="primary"
        :disabled="isSaving || isDisabled"
        @click="onSaveHandler"
        >{{ $t("materials.createEditModal.saveMaterialBtn") }}</eg-button
      >
    </template>
  </eg-modal>
</template>

<script>
import {
  createSourceMaterialFiles,
  createSourceMaterials,
  getSourceMaterial,
  updateSourceMaterials,
} from "@/api";
import EgFormGroup from "@/components/global/eg_form_group.vue";
import TdaSources from "@/components/assignment/tda_sources.vue";
import SourceMaterialPreviewForm from "@/components/source-materials/source_material_preview_form.vue";

function getDuplicates(arr) {
  const seen = new Set();
  const duplicates = [];

  arr.forEach((item) => {
    const identifier = item.name;
    if (seen.has(identifier)) {
      duplicates.push(item);
    } else {
      seen.add(identifier);
    }
  });

  return duplicates;
}

export default {
  name: "CreateEditSourceMaterialModal",
  components: {
    SourceMaterialPreviewForm,
    TdaSources,
    EgFormGroup,
  },
  props: {
    sourceMaterialId: { type: Number, default: null },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      isSaving: false,
      uppyFiles: [],
      editableItem: {
        title: "",
        tdaInstructions: "",
        tdaSources: [],
      },
    };
  },
  computed: {
    modalTitle() {
      if (this.readOnly) {
        return this.$t("materials.createEditModal.materialPreview");
      }
      return this.isEdit
        ? this.$t("materials.createEditModal.editMaterial")
        : this.$t("materials.createEditModal.createNewMaterial");
    },
    isEdit() {
      return !!this.sourceMaterialId;
    },
    isDisabled() {
      if (!this.isEdit) {
        return this.editableItem.title === "";
      }
      return false;
    },
  },
  methods: {
    async onOpenModalHandler() {
      if (this.isEdit) {
        this.isLoading = true;
        const { data } = await getSourceMaterial(this.sourceMaterialId);
        this.editableItem = data;
      }
      this.isLoading = false;
    },
    updateTda(tdaSources) {
      this.editableItem.tdaSources = tdaSources;
    },
    // tdaSources logic copied from assignments
    showAndWaitDuplicateModal(duplicateNameFiles) {
      const h = this.$createElement;
      const titleVNode = h("div", {
        domProps: {
          innerHTML: this.$t("materials.createEditModal.uploadOptions"),
        },
      });
      const messageVNode = h("div", { class: ["foobar"] }, [
        h("div", {}, [
          `${duplicateNameFiles.map((file) => file.name).join(", ")} ${this.$t(
            "materials.createEditModal.alreadyExists"
          )}`,
        ]),
        h("eg-b-form-radio-group", {
          class: ["mt-3"],
          props: {
            stacked: true,
            checked: true,
            name: "duplicate-name",
            options: [
              {
                text: this.$t("materials.createEditModal.replaceExistingFile"),
                value: true,
              },
              {
                text: this.$t("materials.createEditModal.uploadBothFiles"),
                value: false,
              },
            ],
          },
          on: {
            input: (value) => {
              this.replaceExistingFiles = value;
            },
          },
        }),
      ]);
      // We must pass the generated VNodes as arrays
      return this.$bvModal
        .msgBoxConfirm([messageVNode], {
          title: [titleVNode],
          okTitle: this.$t("common.upload"),
          okVariant: "info",
          cancelTitle: this.$t("common.cancel"),
          buttonSize: "sm",
          centered: true,
          size: "md",
        })
        .then((value) => {
          if (!value) {
            return this.$t("materials.createEditModal.deleteDuplicateFiles");
          }
        });
    },
    async onSaveHandler() {
      try {
        if (!this.editableItem.title) {
          this.$showToastError(this.$t("errors.titleRequired"));
          return;
        }
        this.isSaving = true;
        this.editableItem.tdaSources = this.editableItem.tdaSources.filter(
          (source) => source.status !== "Error"
        );
        await this.$refs.tdaSources.upload();

        const duplicateNameFiles = getDuplicates(this.editableItem.tdaSources);

        if (duplicateNameFiles.length > 0) {
          const error = await this.showAndWaitDuplicateModal(
            duplicateNameFiles
          );

          if (error) {
            return this.$showToastError(error);
          }
        }

        const newFilesData = this.editableItem.tdaSources.filter(
          (source) => !source.id
        );

        newFilesData.forEach((source) => {
          source.status = "Uploading...";
        });

        if (newFilesData.length > 0) {
          const { data: newFiles } = await createSourceMaterialFiles(
            newFilesData.map((file) => {
              return {
                name: file.name,
                url: file.fileUrl,
                content: file.content,
              };
            })
          );

          newFilesData.forEach((source, index) => {
            source.id = newFiles[index].id;
            source.status = null;
          });
        }

        const sourcesIds = this.editableItem.tdaSources.map(
          (source) => source.id
        );

        const payload = {
          title: this.editableItem.title,
        };

        if (this.editableItem.tdaInstructions) {
          payload.tdaInstructions = this.editableItem.tdaInstructions;
        }

        if (sourcesIds.length > 0) {
          payload.tdaSources = sourcesIds;
        }
        let result = {};
        if (this.editableItem.id) {
          payload.id = this.editableItem.id;
          result = await updateSourceMaterials(payload);
        } else {
          result = await createSourceMaterials(payload);
        }

        this.$showToastSuccess(
          this.editableItem.id
            ? this.$t("materials.createEditModal.referenceMaterialUpdated")
            : this.$t("materials.createEditModal.referenceMaterialCreated"),
          { title: this.$t("common.success") }
        );
        this.$egModal.hide("create-edit-source-material-modal");
        this.$emit("onSave", result);
      } catch (error) {
        let message = error.response?.data?.error;

        if (error.response?.data?.meta?.message.includes("Data too long")) {
          message = this.$t(
            "materials.createEditModal.oneOfSourcesHasLongData"
          );
          this.innerAssignment.tdaSources
            .filter((source) => !source.id)
            .forEach((source) => {
              source.status = "Error";
            });
        }

        this.$showToastError(message);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style>
.custom-textarea {
  font-size: var(--typography-font-size-fs-300);

  &::placeholder {
    color: var(--color-text-neutral-disabled);
  }
}
</style>
